import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
Copyright 2026 Element Creations Ltd.

Licensed under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/

import { MatrixEvent } from "../models/event.js";
import { RoomEvent } from "./room.js";
import { RoomStateEvent } from "./room-state.js";
import { logger as rootLogger } from "../logger.js";
import { EventType } from "../@types/event.js";
import { ROOM_RETENTION_TYPE } from "../@types/retention.js";

/**
 * Applies https://github.com/matrix-org/matrix-spec-proposals/pull/1763 by checking the current
 * state of the room and applying the *latest* retention policy to all preceeding events.
 * Currently does not:
 *   - Apply global server policies (requires Synapse work)
 *   - React at all to `min_retention` configs.
 *   - Loop
 */
export class RoomRetentionPolicy {
  constructor(room, retentionService, store) {
    var _this = this;
    this.room = room;
    this.retentionService = retentionService;
    this.store = store;
    // minRetention is not implemented.
    _defineProperty(this, "maxRetention", null);
    _defineProperty(this, "retentionTimeout", void 0);
    _defineProperty(this, "logger", void 0);
    _defineProperty(this, "handleRetentionUpdate", /*#__PURE__*/_asyncToGenerator(function* () {
      // First store if we currently have a retention period.
      var hadRetentionPeriod = _this.maxRetention !== null;
      try {
        yield _this.recalculateRetention(_this.room.currentState);
      } catch (err) {
        _this.logger.warn("Failed to recalculate retention policy", err);
        return;
      }

      // Bind/unbind the Timeline event handler based on whether we should be running retention.
      if (hadRetentionPeriod !== (_this.maxRetention !== null)) {
        _this.logger.info("retention recalculated to be", _this.maxRetention);
        // We've changed
        if (_this.maxRetention) {
          _this.room.on(RoomEvent.Timeline, _this.timelineUpdated);
        } else {
          _this.room.off(RoomEvent.Timeline, _this.timelineUpdated);
        }
      }
    }));
    _defineProperty(this, "recalculateRetention", /*#__PURE__*/function () {
      var _ref2 = _asyncToGenerator(function* (roomState) {
        var _serverPolicy$policie, _unstableEvent$getCon, _ref3, _serverPolicy$policie2, _serverPolicy$limits2;
        var unstableEvent = roomState.getStateEvents(ROOM_RETENTION_TYPE.name).find(e => e.getStateKey() === "");
        var stableEvent = roomState.getStateEvents(ROOM_RETENTION_TYPE.altName).find(e => e.getStateKey() === "");
        var serverPolicy = _this.retentionService.getCached();
        var serverRoomPolicy = serverPolicy === null || serverPolicy === void 0 || (_serverPolicy$policie = serverPolicy.policies) === null || _serverPolicy$policie === void 0 ? void 0 : _serverPolicy$policie[_this.room.roomId];
        var roomStatePolicy = (_unstableEvent$getCon = unstableEvent === null || unstableEvent === void 0 ? void 0 : unstableEvent.getContent()) !== null && _unstableEvent$getCon !== void 0 ? _unstableEvent$getCon : stableEvent === null || stableEvent === void 0 ? void 0 : stableEvent.getContent();
        var content = // * if the homeserver defines a specific retention policy for this room, then use this policy as the effective retention policy of the room.
        (_ref3 = serverRoomPolicy !== null && serverRoomPolicy !== void 0 ? serverRoomPolicy :
        // * otherwise, if the state of the room includes a `m.room.retention` event with an empty state key:
        roomStatePolicy) !== null && _ref3 !== void 0 ? _ref3 : // * otherwise, if the state of the room does not include a m.room.retention event with an empty state key:
        serverPolicy === null || serverPolicy === void 0 || (_serverPolicy$policie2 = serverPolicy.policies) === null || _serverPolicy$policie2 === void 0 ? void 0 : _serverPolicy$policie2["*"];
        if (!content) {
          // * otherwise, don't apply a retention policy in this room.
          _this.maxRetention = null;
          return;
        }
        var validatePolicy = !serverRoomPolicy && roomStatePolicy;

        // Parse it
        var maxLifetime = content.max_lifetime;
        if (!maxLifetime && validatePolicy) {
          var _serverPolicy$limits;
          // if there is no value specified in the room's state, use the limit's min value for the effective retention policy of the room (which can be null or absent).
          maxLifetime = serverPolicy === null || serverPolicy === void 0 || (_serverPolicy$limits = serverPolicy.limits) === null || _serverPolicy$limits === void 0 || (_serverPolicy$limits = _serverPolicy$limits.max_lifetime) === null || _serverPolicy$limits === void 0 ? void 0 : _serverPolicy$limits.min;
          if (!maxLifetime) {
            _this.maxRetention = null;
            return;
          }
        }
        if (typeof maxLifetime !== "number") {
          throw Error("max_lifetime must be a number, got \"".concat(maxLifetime, "\""));
        }
        if (maxLifetime < 0 || !Number.isInteger(maxLifetime)) {
          throw Error("max_lifetime must be >= 0, got ".concat(maxLifetime));
        }
        _this.maxRetention = maxLifetime;
        if (validatePolicy && serverPolicy !== null && serverPolicy !== void 0 && (_serverPolicy$limits2 = serverPolicy.limits) !== null && _serverPolicy$limits2 !== void 0 && _serverPolicy$limits2.max_lifetime) {
          var _serverPolicy$limits$, _serverPolicy$limits$2;
          // We're using room state so we need to validate this policy matches the server.
          /**
          if the value specified in the room's state complies with the limit, use this value for the effective retention policy of the room.
          if the value specified in the room's state is lower than the limit's min value, use the min value for the effective retention policy of the room.
          if the value specified in the room's state is greater than the limit's max value, use the max value for the effective retention policy of the room.
          */
          _this.maxRetention = Math.max(_this.maxRetention, (_serverPolicy$limits$ = serverPolicy.limits.max_lifetime.min) !== null && _serverPolicy$limits$ !== void 0 ? _serverPolicy$limits$ : 0);
          _this.maxRetention = Math.min(_this.maxRetention, (_serverPolicy$limits$2 = serverPolicy.limits.max_lifetime.max) !== null && _serverPolicy$limits$2 !== void 0 ? _serverPolicy$limits$2 : Number.MAX_SAFE_INTEGER);
        }
        _this.processTimeline();
      });
      return function (_x) {
        return _ref2.apply(this, arguments);
      };
    }());
    _defineProperty(this, "timelineUpdated", () => {
      this.logger.info("timelineUpdated");
      this.scheduleTimelineCheck(200);
    });
    _defineProperty(this, "scheduleTimelineCheck", nextTs => {
      if (this.retentionTimeout) {
        clearTimeout(this.retentionTimeout);
      }
      this.retentionTimeout = setTimeout(() => {
        this.processTimeline();
        this.retentionTimeout = undefined;
      }, nextTs);
    });
    this.logger = rootLogger.getChild("RetentionPolicy ".concat(room.roomId));

    // Recalculate on room state update.
    room.on(RoomStateEvent.Events, event => {
      if (event.getStateKey() !== "" || event.getType() !== "org.matrix.msc1763.retention" && event.getType() !== "m.room.retention") {
        return;
      }
      void this.handleRetentionUpdate();
    });

    // Recalculate our retention whenever the global policy changes.
    this.retentionService.on("update", () => {
      this.logger.info("Got global retention policy update!");
      void this.handleRetentionUpdate();
    });
    // Do an initial check on construction.
    void this.handleRetentionUpdate();

    // Only bind RoomEvent.Timeline once we know we have a retention policy.
  }
  shouldEventBeRetained(ev) {
    if (!this.maxRetention) {
      return true;
    }
    var expireBefore = Date.now() - this.maxRetention;
    return ev.getTs() > expireBefore;
  }
  processTimeline() {
    if (!this.maxRetention) {
      return; // No policy, skip.
    }
    this.logger.info("Running processTimeline");
    var expireBefore = Date.now() - this.maxRetention;
    var events = this.room.getLiveTimeline().getEvents().filter(ev => ev.getStateKey() === undefined && !ev.isRedacted() && ev.getType() !== "m.room.redaction");
    var expiredEvents = events.filter(ev => ev.getTs() < expireBefore);
    var _events$filter$sort = events.filter(ev => ev.getTs() >= expireBefore).sort((a, b) => a.getTs() - b.getTs()),
      _events$filter$sort2 = _slicedToArray(_events$filter$sort, 1),
      earliestExpiringEvent = _events$filter$sort2[0];
    if (earliestExpiringEvent) {
      var nextTs = earliestExpiringEvent.getTs() + this.maxRetention - Date.now();
      this.logger.info("Next expiry scheduled for ".concat(nextTs));
      this.scheduleTimelineCheck(nextTs);
    }
    if (expiredEvents.length === 0) {
      this.logger.info("Found no expired events");
      return;
    }
    this.logger.info("Found ".concat(expiredEvents.length, " expired events"));
    for (var event of expiredEvents) {
      this.room.tryApplyRedaction(new MatrixEvent({
        type: EventType.RoomRedaction,
        sender: event.getSender(),
        event_id: "$synthetic_redaction",
        room_id: this.room.roomId,
        redacts: event.getId(),
        content: {
          reason: "Retention policy"
        },
        origin_server_ts: Date.now(),
        unsigned: {}
      }));
    }
    void this.store.removeEventsFromRoom(this.room.roomId, expiredEvents.map(e => e.getId())).catch(ex => {
      this.logger.warn("Failed to remove events from store", ex);
    });
  }
}
//# sourceMappingURL=room-retention.js.map